import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  buildOpportunityPayload,
  type EntryPointConfig,
  type OpportunityType,
} from "@/lib/opportunityPayload";
import {
  asSubmittedResponse,
  submitOperatorOpportunity,
  type OperatorIntakeResult,
} from "@/lib/operatorIntake";
import { trackPublicFunnelEvent } from "@/lib/publicFunnel";

interface OfferInterestSignupProps {
  config: EntryPointConfig;
}

interface OfferInterestCopy {
  eyebrow: string;
  title: string;
  description: string;
  contentTrack: string;
  cta: string;
  contextLabel: string;
  contextPlaceholder: string;
  defaultContext: string;
  successTitle: string;
}

const OptionalBadge = () => (
  <>
    <span className="sr-only"> optional</span>
    <span aria-hidden="true" className="ml-2 text-xs font-medium text-slate-500">Optional</span>
  </>
);

const copyByOpportunityType: Record<OpportunityType, OfferInterestCopy> = {
  AI_OPERATOR_BUILD: {
    eyebrow: "Operator field notes",
    title: "Get the Operator field notes.",
    description:
      "Not ready to scope an Operator yet? Join the list for practical notes on intake, follow-up, approvals, and operating visibility while the public offer sharpens.",
    contentTrack: "SVM Operator field notes",
    cta: "Get Operator field notes",
    contextLabel: "What kind of work are you trying to keep organized?",
    contextPlaceholder: "For example: client requests, approvals, delivery updates, or weekly follow-up.",
    defaultContext: "Wants practical notes about organizing intake, follow-up, approvals, and delivery visibility.",
    successTitle: "You're on the Operator notes list.",
  },
  BUSINESS_SYSTEMS_AUDIT: {
    eyebrow: "Systems audit checklist",
    title: "Get the audit checklist.",
    description:
      "If you are not ready for a full audit, get the practical notes SVM is preparing on finding leaks, repeated work, missing ownership, and slow handoffs.",
    contentTrack: "systems audit checklist",
    cta: "Get the audit checklist",
    contextLabel: "Where does work feel leaky or too manual?",
    contextPlaceholder: "For example: missed leads, repeated data entry, slow approvals, or unclear ownership.",
    defaultContext: "Wants practical systems-audit notes before requesting a full review.",
    successTitle: "You're on the audit notes list.",
  },
  APP_BUILD: {
    eyebrow: "Build launch notes",
    title: "Get the build launch notes.",
    description:
      "Still shaping the idea? Join the list for first-release strategy, scope decisions, and launch-readiness notes before you ask SVM to build.",
    contentTrack: "app build launch notes",
    cta: "Get build launch notes",
    contextLabel: "What are you thinking about building?",
    contextPlaceholder: "For example: a customer portal, staff workflow app, internal tool, or focused MVP.",
    defaultContext: "Wants app-build launch notes before submitting a build request.",
    successTitle: "You're on the build notes list.",
  },
  APP_RESCUE: {
    eyebrow: "App Rescue launch notes",
    title: "Get the App Rescue launch notes.",
    description:
      "Not ready to submit the app yet? Join the list for stuck-app teardown notes, launch-readiness checks, and early access when diagnostic booking opens.",
    contentTrack: "App Rescue launch notes",
    cta: "Join App Rescue notes",
    contextLabel: "What kind of app problem are you watching?",
    contextPlaceholder: "For example: auth, payments, deployment, data trust, UX confusion, or launch scope.",
    defaultContext: "Wants App Rescue launch notes and booking-open updates.",
    successTitle: "You're on the App Rescue list.",
  },
  VENTURE_DEAL_EVALUATION: {
    eyebrow: "Evaluation notes",
    title: "Get the evaluation notes.",
    description:
      "If the asset, domain, partnership, or deal is still early, get SVM's decision notes on upside, constraints, commitment, and when to move or pass.",
    contentTrack: "venture evaluation notes",
    cta: "Get evaluation notes",
    contextLabel: "What kind of opportunity are you evaluating?",
    contextPlaceholder: "For example: a domain, app, audience, partnership, acquisition, or client deal.",
    defaultContext: "Wants venture-evaluation notes before submitting a decision request.",
    successTitle: "You're on the evaluation notes list.",
  },
  GENERAL_INQUIRY: {
    eyebrow: "SVM launch notes",
    title: "Get the SVM notes.",
    description:
      "Get practical notes on apps, operators, systems, and clearer technology decisions while SVM prepares more public material.",
    contentTrack: "SVM launch notes",
    cta: "Get SVM notes",
    contextLabel: "What would you like to learn more about?",
    contextPlaceholder: "Apps, operators, systems, audits, venture decisions, or something else.",
    defaultContext: "Wants general SVM launch notes.",
    successTitle: "You're on the SVM notes list.",
  },
};

function newSubmissionId(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return `offer-interest-${cryptoObj.randomUUID()}`;
  }
  return `offer-interest-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const OfferInterestSignup = ({ config }: OfferInterestSignupProps) => {
  const [email, setEmail] = useState("");
  const [context, setContext] = useState("");
  const [consented, setConsented] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<OperatorIntakeResult | null>(null);
  const submissionId = useRef(newSubmissionId());
  const hasStarted = useRef(false);
  const successRef = useRef<HTMLDivElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const copy = copyByOpportunityType[config.opportunityType];
  const submittedResponse = result?.mode === "submitted" ? asSubmittedResponse(result.response) : null;
  const signupSubmitted = result?.mode === "submitted";
  const signupId = useMemo(() => `${config.path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "svm"}-notes`, [config.path]);
  const analyticsSignupType = config.opportunityType === "APP_RESCUE" ? "updates" : "offer_interest";
  const signupEventPrefix = analyticsSignupType === "updates" ? "updates_signup" : "offer_interest_signup";
  const subscriptionIntent = analyticsSignupType === "updates" ? "app_rescue_launch_notes" : "offer_launch_notes";

  const trackSignupEvent = (name: string, extra: Record<string, string | number | boolean | undefined> = {}) => {
    trackPublicFunnelEvent(name, {
      service: config.opportunityType,
      entry_point: config.entryPoint,
      route: config.path,
      offer_track: copy.contentTrack,
      signup_type: analyticsSignupType,
      subscription_intent: subscriptionIntent,
      ...extra,
    });
  };

  const markStarted = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    trackSignupEvent(`${signupEventPrefix}_started`);
  };

  useEffect(() => {
    if (!result) return;

    window.setTimeout(() => {
      successRef.current?.scrollIntoView({ block: "start" });
      successHeadingRef.current?.focus({ preventScroll: true });
    }, 0);
  }, [result]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValidEmail(email)) {
      setError("Enter a valid email so SVM can send the notes.");
      return;
    }

    if (!consented) {
      setError("Confirm that SVM may send you this offer track before joining.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    const visitorContext = context.trim() || copy.defaultContext;
    const payload = buildOpportunityPayload({
      config,
      form: {
        name: "Launch Notes Subscriber",
        email: email.trim(),
        summary: `Offer interest signup for ${config.title}. ${visitorContext}`,
        desiredOutcome: `Receive ${copy.contentTrack} and launch updates from Shock Value Media.`,
        timeline: "Preparing for launch",
        budgetRange: "Not sure yet",
        urgency: "Low - information gathering",
      },
      details: {
        signupType: "offer_interest",
        analyticsSignupType,
        newsletterInterest: "true",
        subscriptionIntent,
        lifecycleStage: "nurture",
        leadIntent: "more_information",
        offerTitle: config.title,
        offerPath: config.path,
        offerTrack: copy.contentTrack,
        visitorContext,
      },
      submissionId: submissionId.current,
    });

    trackSignupEvent(`${signupEventPrefix}_attempted`, {
      has_context: Boolean(context.trim()),
      consented,
    });

    try {
      const nextResult = await submitOperatorOpportunity(payload);
      const submitted = nextResult.mode === "submitted" ? asSubmittedResponse(nextResult.response) : null;

      setResult(nextResult);
      trackSignupEvent(`${signupEventPrefix}_submitted`, {
        mode: nextResult.mode,
        duplicate: submitted?.duplicate,
        operator_status: submitted?.status,
        has_reference: Boolean(submitted?.opportunity_id),
      });
    } catch (caught) {
      trackSignupEvent(`${signupEventPrefix}_failed`, { reason: "submit_error" });
      setError(caught instanceof Error ? caught.message : "We could not add you right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id={signupId} className="border-t border-slate-300 bg-[#fffdf6] py-12 md:py-16">
      <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-[0.92fr_1.08fr] lg:items-center lg:px-8">
        <div>
          <p className="mb-3 inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">
            {copy.eyebrow}
          </p>
          <h2 className="svm-display-tight max-w-2xl text-4xl leading-tight text-slate-950 md:text-5xl">
            {copy.title}
          </h2>
          <p className="mt-4 max-w-2xl font-medium leading-7 text-slate-700">{copy.description}</p>
          <div className="mt-6 grid gap-3 text-sm font-semibold leading-6 text-slate-700 sm:grid-cols-3">
            {["Offer-specific notes", "Early launch updates", "No credentials or spam"].map((item) => (
              <div key={item} className="flex items-start gap-2 border-t border-slate-200 pt-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-slate-300 bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)] md:p-7">
          {result ? (
            <div ref={successRef} aria-live="polite" className="min-h-64 scroll-mt-28">
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                <Mail className="h-6 w-6" />
              </span>
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
                {signupSubmitted ? "Signup received" : "Signup preview ready"}
              </p>
              <h3 ref={successHeadingRef} tabIndex={-1} className="svm-display-tight text-3xl text-slate-950 outline-none">
                {signupSubmitted ? copy.successTitle : "Your signup is prepared."}
              </h3>
              <p className="mt-4 font-medium leading-7 text-slate-700">
                {signupSubmitted
                  ? `We will send useful ${copy.contentTrack.toLowerCase()} and launch updates as this offer gets sharper.`
                  : result.message}
              </p>
              {submittedResponse?.opportunity_id && (
                <p className="mt-4 text-sm font-bold text-slate-700">Reference: {submittedResponse.opportunity_id}</p>
              )}
              <a
                href="#opportunity-intake"
                className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-slate-950 underline decoration-amber-400 decoration-2 underline-offset-4 hover:text-rose-700"
                onClick={() => trackSignupEvent(`${signupEventPrefix}_primary_form_clicked`, { placement: "signup_success" })}
              >
                Want help sooner? Send the project context.
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          ) : (
            <form className="grid gap-5" onSubmit={handleSubmit} noValidate>
              <div>
                <h3 className="text-2xl font-black text-slate-950">Not ready to send the full brief?</h3>
                <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
                  Join the right track and SVM can follow up with useful context before the full launch motion is ready.
                </p>
              </div>

              <label htmlFor={`${signupId}-email`} className="grid gap-2 text-sm">
                <span className="font-bold text-slate-950">Email</span>
                <Input
                  id={`${signupId}-email`}
                  type="email"
                  autoComplete="email"
                  value={email}
                  placeholder="you@company.com"
                  onChange={(event) => {
                    markStarted();
                    setError("");
                    setEmail(event.target.value);
                  }}
                />
              </label>

              <label htmlFor={`${signupId}-context`} className="grid gap-2 text-sm">
                <span className="font-bold text-slate-950">
                  {copy.contextLabel} <OptionalBadge />
                </span>
                <Textarea
                  id={`${signupId}-context`}
                  value={context}
                  placeholder={copy.contextPlaceholder}
                  className="min-h-24 resize-y"
                  onChange={(event) => {
                    markStarted();
                    setContext(event.target.value);
                  }}
                />
              </label>

              <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm font-medium leading-6 text-slate-700">
                <input
                  type="checkbox"
                  required
                  checked={consented}
                  onChange={(event) => {
                    markStarted();
                    setError("");
                    setConsented(event.target.checked);
                  }}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-400 text-rose-600 focus:ring-rose-500"
                />
                <span>
                  Send me {copy.contentTrack.toLowerCase()} and related SVM launch updates. I understand this is a
                  lower-pressure information track, not a request for credentials or private data.
                </span>
              </label>

              {error && (
                <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-800">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || !consented}
                className="w-full gap-2 rounded-md bg-slate-950 font-bold text-white hover:bg-rose-700 disabled:opacity-60 sm:w-auto sm:justify-self-end"
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? "Joining..." : copy.cta}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default OfferInterestSignup;
